import React from "react";
import ReactMarkdown from "react-markdown";

const md = `
## OptiPrime™ License Terms (Academic & Nonprofit Use)
© 2025 The Broad Institute, Inc., ("we" or "Institutions").
All rights reserved except as expressly granted below.
### Definitions
- **Code:** Software implementing and/or providing access to OptiPrime, including any source code, object code, models, model parameters, configurations, and documentation.
- **Outputs:** Anything the Code generates (designs, predictions, rankings, etc.).
- **Background IP:** Institutions’ existing and future patent applications and patents (no license to Background IP is granted by these License Terms or by making the Code available to you).
- **Developments:** Anything created or generated by You using the Code and/or Outputs including any derivatives, modifications, or improvements of, or developments made using, Code and/or Outputs.
- **Generated IP:** All intellectual property rights in Developments.
### Your Agreement
By using the Code or Outputs, You and your employer/organization are jointly and severally bound to these terms.
Any employment-related use automatically binds your employer.
"You" includes both you and your employer/organization, who cannot claim rights in Generated IP without accepting all of the License Terms provided herein.
### Who Can Use This
Free for students, postdocs, faculty, and staff at academic, nonprofit, or U.S. government research institutions (“Academic & Nonprofit Users”).
**Commercial users** (including any users for industry-sponsored research) must contact [legal@broadinstitute.org](mailto:legal@broadinstitute.org) for a separate license.
### What You Can Do
*Subject to these License Terms*, You may:
- use and modify the Code and Outputs solely for **non-profit research and education purposes**
(use in industry-sponsored research requires a separate license from Institutions)
- pursue protection of, and license, your respective interest in any Generated IP
- publish Outputs subject to the Attribution clause below
### What You Can't Do
**The following restrictions apply to Code, Outputs, and Developments:**
- **No commercial use** without separate license, including any use in industry-sponsored research
- **No disclosing/publishing/sharing/distribution** *except, along with and subject to these License Terms, to*: 
  - Anyone via scientific or academic publications, or published patent applications or patents
  - Other Academic & Nonprofit Users, but solely to the extent they agree their use of the Code or Output is subject to these License Terms
- **No misleading others** about origin or using for illegal/harmful purposes
- **No using Institutions’ names/logos** without permission

**Attribution:** When publishing results, please cite the OptiPrime paper:
\`\`\`
@article{optiprime,
    title     = {Mechanistic machine learning for prediction of prime editing outcomes},
    author    = {Hsu, Alvin and Chen, Peter J. and Li, Angus H. and Hemez, Colin F. and Gao, Xin D.
                 and Terrey, Markus and Nelson, Charlie and Selvam, Vijay and Cristian, Ana and
                 McElroy, Amber N. and Steinbeck, Benjamin J. and Mahadeshwar, Gandhar K. and
                 Pandey, Smriti and Barsdale, Zachary and Chen, Paul Z. and Sousa, Alexander A. and
                 Sakai, Holt A. and Silverstein, Rachel A. and Morad, Ilias and Krueger, Ryan K. and
                 Shen, Max W. and Kleinstiver, Benjamin P. and Lutz, Cathleen M. and Tolar, Jakub
                 and Blazar, Bruce R. and Osborn, Mark J. and Liu, David R.},
    author+an = {1=jointfirst;2=jointfirst;3=jointfirst},
    year      = 2026,
    journal   = {Nature Biotechnology},
    type      = {Journal Article}
}
\`\`\`
You must include a notice that any Code, Outputs, Developments, and Generated IP are subject to these License Terms.

### Conditions on Background IP and Generated IP:
- No license is granted to Background IP, except for the minimum rights necessary to use or modify the Code. 
- You hereby grant Institutions, and their respective licensees, a perpetual, irrevocable, worldwide, paid-up, royalty-free, sublicensable, non-exclusive license to all Generated IP for any purpose.
- You are prohibited from asserting infringement of Generated IP against Institutions or their respective licensees. Asserting such Generated IP terminates this license automatically and the Termination clause applies.

**Safety & Compliance:** The Code and Outputs are **research tools only** - they are not validated for clinical use or professional advice. You must comply with all applicable laws and regulations.

**Logging & Enforcement:** We log OptiPrime queries and associated metadata. We use these records to operate and secure the Code, to monitor compliance with these License Terms, and to establish and enforce Institutions' rights.

**Termination:** Your rights terminate automatically (retroactively to first use of the Code or Outputs) if you breach these License Terms. You must then stop use and delete local copies where feasible.

**Disclaimers; Limited Liability:**
Everything is provided "AS IS" at your own risk.
We disclaim all warranties including merchantability, fitness for a particular purpose, and non-infringement.
We are not liable for indirect damages or lost profits.
You will indemnify, defend, and hold harmless Institutions from claims arising from your use of the
Code, Outputs, Developments, and any Generated IP, breach of the License Terms, or legal violations.

**Updates & Contact:** We may update these License Terms by posting a new version.
Continued use of the Code or Outputs means acceptance.
**Questions?** Please contact [legal@broadinstitute.org](mailto:legal@broadinstitute.org)

*License Terms effective: September 8, 2025*
`;

const TOS = () => {
    return <ReactMarkdown children={md} />
}

export default TOS;
